import { AxiosInstance } from 'axios'
import {
  ChartReimbursementsResponse,
  DownloadPayPeriodResponse,
  PayPeriod,
  PayPeriodActions,
  PayPeriodIssues,
  PayPeriodStatus
} from 'types/pay-periods'
import { PaginatedResult } from 'types/utility'
import { cleanObject } from 'utils/Functions'
import { PAY_PERIODS_ENDPOINTS } from 'api/ApiRoutes'
import { PayPeriodBulkActionResponse } from 'types/schedules'
import { JobStatus } from 'types/misc'
import { Team } from 'types/groups'
import { Thread, ThreadPayload } from 'types/assistant'

class PayPeriodsApi {
  constructor(private axiosInstance: AxiosInstance) {}

  getPayPeriods = async ({
    status = '',
    kind = '',
    pageSize = 10,
    page = 1,
    issues = undefined
  }: {
    status?: `${PayPeriodStatus}` | '' | 'active'
    kind?: keyof PayPeriod['kind'] | ''
    issues?: PayPeriodIssues
    pageSize: number
    page: number
  }) => {
    const d = await this.axiosInstance.get<PaginatedResult<PayPeriod>>(
      PAY_PERIODS_ENDPOINTS.BASE,
      {
        params: cleanObject(
          issues
            ? { page_size: pageSize, page, kind, issues }
            : { status, page_size: pageSize, page, kind }
        )
      }
    )
    return d.data
  }

  getPayPeriod = async ({ id }: { id: PayPeriod['id'] }) => {
    const d = await this.axiosInstance.get<PayPeriod>(
      PAY_PERIODS_ENDPOINTS.PAY_PERIOD(id)
    )
    return {
      ...d.data,
      kind: d.data.kind ?? 'variable'
    }
  }

  BatchUpdatePayPeriodReports = async ({
    id,
    action
  }: {
    id: PayPeriod['id']
    action: PayPeriodActions
  }) => {
    const d = await this.axiosInstance.post<PayPeriodBulkActionResponse>(
      PAY_PERIODS_ENDPOINTS.PAY_PERIOD_STATUS(id),
      {
        pay_period_action: action
      }
    )
    return d.data
  }

  payPeriodSummaryDownload = async ({
    pay_period_ids,
    include_trips,
    include_transactions
  }: {
    pay_period_ids: PayPeriod['id'][]
    include_trips: boolean
    include_transactions: boolean
  }) => {
    const d = await this.axiosInstance.post<JobStatus>(
      PAY_PERIODS_ENDPOINTS.PAY_PERIODS_DOWNLOAD,
      {
        pay_period_ids,
        include_trips,
        include_transactions
      }
    )
    return d.data
  }

  fundingRequestDownload = async (periods: PayPeriod['id'][]) => {
    const d = await this.axiosInstance.post<JobStatus>(
      PAY_PERIODS_ENDPOINTS.PAY_PERIOD_FUNDING_DOWNLOAD,
      {
        pay_period_ids: periods
      }
    )
    return d.data
  }

  downloadPayPeriod = async ({
    pay_period_id,
    include_trips = false,
    include_transactions = false
  }: {
    pay_period_id: PayPeriod['id']
    include_trips?: boolean
    include_transactions?: boolean
  }) => {
    const d = await this.axiosInstance.post<DownloadPayPeriodResponse>(
      PAY_PERIODS_ENDPOINTS.PAY_PERIOD_DOWNLOAD(pay_period_id),
      {
        include_trips,
        include_transactions
      }
    )
    return d.data
  }

  getChartReimbursements = async ({
    teamId,
    year
  }: {
    teamId: Team['token_id']
    year: number
  }) => {
    const { data } = await this.axiosInstance.get<ChartReimbursementsResponse>(
      PAY_PERIODS_ENDPOINTS.REIMBURSEMENTS_CHARTS(teamId),
      {
        params: {
          year
        }
      }
    )
    return data
  }

  requestPayPeriodThread = async (payload: ThreadPayload) => {
    const { data } = await this.axiosInstance.put<{ data: Thread[] }>(
      PAY_PERIODS_ENDPOINTS.ASSISTANT,
      { ...payload }
    )
    return data.data
  }

  getPayPeriodThread = async (payload: {
    pay_period_id: string
    after?: string
    limit?: number
  }) => {
    const { data } = await this.axiosInstance.get<{
      data: Thread[]
      pagination: { first_id: string; last_id: string; has_more: boolean }
    }>(PAY_PERIODS_ENDPOINTS.ASSISTANT, {
      params: { ...payload, limit: payload?.limit ?? 10 }
    })
    return data
  }
}

export default PayPeriodsApi
